import type { FormEvent } from 'react';
import React from 'react';
import { Formik } from 'formik';
import isObjectLike from 'lodash/isObjectLike';
import uniq from 'lodash/uniq';
import get from 'lodash/get';
import has from 'lodash/has';
import isEqual from 'lodash/isEqual';
import type { z } from 'zod';

import { FormSchemaUtil } from '../../../utils/form-schema-util';

import { RawForm } from './RawForm';
import type { FormProps } from './form.types';

type GenericFormObjectType = { [key: string | number]: unknown };

export type { FormikProps, FormikHelpers } from 'formik';

export const isDifferent = (value: unknown, compareAgainst: unknown) => {
  if (isObjectLike(value) && isObjectLike(compareAgainst)) {
    try {
      return !isEqual(value, compareAgainst);
    } catch {
      return true;
    }
  }
  return value !== compareAgainst;
};

export const filterOutUnchangedData = (
  initialData?: unknown,
  finalData?: unknown
) => {
  if (!initialData || !isObjectLike(initialData)) {
    return finalData;
  }

  let allKeys = uniq(Object.keys(finalData as Record<string, unknown>));
  if (allKeys.length === 0 && Object.keys(initialData).length > 0) {
    allKeys = uniq(Object.keys(initialData));
  }
  return allKeys.reduce((acc: GenericFormObjectType, propName) => {
    if (
      (!has(initialData, propName) && has(finalData, propName)) ||
      (has(initialData, propName) &&
        isDifferent(get(finalData, propName), get(initialData, propName)))
    ) {
      acc[propName] = get(finalData, propName);
    }

    return acc;
  }, {});
};

export const Form = <Schema extends z.ZodTypeAny>({
  cancelOnEscape,
  children,
  initialValues,
  name = 'Form',
  onChange,
  onSubmit,
  schema,
  submitOnChange,
  formRef,
}: FormProps<Schema>) => {
  return (
    <Formik<z.infer<Schema>>
      validate={values => {
        const { errors } = FormSchemaUtil.validate(schema, values);
        if (process.env.NODE_ENV === 'development' && errors) {
          // eslint-disable-next-line no-console
          console.log('(local only) Validation Info:', { errors, values });
        }
        return errors || {};
      }}
      initialValues={initialValues}
      onSubmit={async (formData, actions) => {
        const { values } = FormSchemaUtil.validate(schema, formData);
        const changedData = filterOutUnchangedData(
          initialValues,
          values
        ) as Partial<z.infer<Schema>>;

        if (Object.keys(changedData).length === 0 && submitOnChange) {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.log(
              '(local only) Form submitted without any changes but its submitOnChange so consuming'
            );
          }
          /**
           * We're doing an instant resolve promise here so that the function we send into
           * Formik is always an async one, which will trigger some internal logic around
           * when it notifies us that the Form is in submitting mode.
           */
          await Promise.resolve();
          return;
        }

        return onSubmit?.(changedData, actions, values);
      }}
      enableReinitialize // allow form selection to update if initialValues change
      innerRef={formRef}
    >
      {({
        dirty,
        handleReset,
        handleSubmit,
        isSubmitting,
        isValid,
        submitCount,
        setTouched,
        setFieldTouched,
        setValues,
        values,
      }) => {
        return (
          <RawForm
            cancelOnEscape={cancelOnEscape}
            dirty={dirty}
            handleReset={handleReset}
            handleSubmit={event => {
              handleSubmit(event as FormEvent<HTMLFormElement>);
            }}
            isSubmitting={isSubmitting}
            isValid={isValid}
            name={name}
            onChange={onChange}
            schema={schema}
            setTouched={setTouched}
            setFieldTouched={setFieldTouched}
            setValues={setValues}
            submitCount={submitCount}
            submitOnChange={submitOnChange}
            values={values}
          >
            {children}
          </RawForm>
        );
      }}
    </Formik>
  );
};
